import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircleOutline,
  OpenInNew,
  PlayArrow,
} from "@mui/icons-material";

import {
  useGetTariffEngineeringCandidateQuery,
  useRunTariffEngineeringResearchMutation,
} from "@/services/tariff-engineering";
import {
  TariffEngineeringOpportunity,
  TariffEngineeringStrategy,
  TariffEngineeringConfidence,
} from "@/types/tariff-engineering";

const strategyColor = (s: TariffEngineeringStrategy): string => {
  switch (s) {
    case "exclusion":
      return "#2e7d32"; // green
    case "reclassify":
      return "#1565c0"; // blue
    case "substantial_transformation":
      return "#6a1b9a"; // purple
    case "conditional_ch99_exclusion":
      return "#c79100"; // gold
    case "metal_breakdown":
      return "#ef6c00"; // orange
    case "other":
      return "#616161"; // grey
  }
};

const strategyLabel = (s: TariffEngineeringStrategy): string => {
  switch (s) {
    case "reclassify":
      return "Reclassify";
    case "substantial_transformation":
      return "Substantial Transformation";
    case "conditional_ch99_exclusion":
      return "Conditional Ch99 Exclusion";
    case "exclusion":
      return "Exclusion List";
    case "metal_breakdown":
      return "Metal Breakdown";
    case "other":
      return "Other";
  }
};

const confidenceColor = (
  c: TariffEngineeringConfidence
): "default" | "info" | "warning" | "success" => {
  switch (c) {
    case "high":
      return "success";
    case "medium":
      return "info";
    case "low":
      return "warning";
  }
};

const fmtUsd = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const OpportunityRow: FC<{ o: TariffEngineeringOpportunity }> = ({ o }) => (
  <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ mb: 1.5, flexWrap: "wrap" }}
    >
      <Chip
        label={strategyLabel(o.strategy)}
        size="small"
        sx={{
          backgroundColor: strategyColor(o.strategy),
          color: "white",
          fontWeight: 600,
        }}
      />
      <Chip
        label={`${o.confidence.toUpperCase()} confidence`}
        size="small"
        color={confidenceColor(o.confidence)}
      />
      <Typography variant="body2" sx={{ ml: "auto !important" }}>
        <Box component="span" sx={{ color: "text.secondary" }}>
          {o.currentRate}% →
        </Box>{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {o.projectedRate}%
        </Box>{" "}
        <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>
          (−{o.savingsPct.toFixed(1)}pp)
        </Box>
      </Typography>
    </Stack>

    {(o.altHts || o.altOrigin || o.altCh99Code) && (
      <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
        {o.altHts && (
          <Typography variant="body2">
            <strong>Alt HTS:</strong>{" "}
            <Box component="span" fontFamily="monospace">
              {o.altHts}
            </Box>
          </Typography>
        )}
        {o.altOrigin && (
          <Typography variant="body2">
            <strong>Alt Origin:</strong> {o.altOrigin}
          </Typography>
        )}
        {o.altCh99Code && (
          <Typography variant="body2">
            <strong>Alt Ch99:</strong>{" "}
            <Box component="span" fontFamily="monospace">
              {o.altCh99Code}
            </Box>
          </Typography>
        )}
      </Stack>
    )}

    <Typography variant="body2" sx={{ mb: 1.5 }}>
      {o.rationale}
    </Typography>

    {o.conditionQuote && (
      <Box
        sx={{
          borderLeft: 3,
          borderColor: strategyColor(o.strategy),
          pl: 1.5,
          my: 1.5,
          fontStyle: "italic",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          “{o.conditionQuote}”
        </Typography>
      </Box>
    )}

    {o.supportingRulings.length > 0 && (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Supporting rulings:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
          {o.supportingRulings.map((r) => (
            <Chip
              key={r.id}
              label={r.id}
              size="small"
              variant="outlined"
              icon={<OpenInNew sx={{ fontSize: 14 }} />}
              onClick={() => window.open(r.url, "_blank", "noopener,noreferrer")}
              clickable
            />
          ))}
        </Stack>
      </Box>
    )}

    {o.tradeDataEvidence && (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Trade-data evidence: {o.tradeDataEvidence.country}{" "}
        {(o.tradeDataEvidence.percentShare * 100).toFixed(1)}% share /{" "}
        {fmtUsd(o.tradeDataEvidence.totalValue)} annual
      </Typography>
    )}

    {o.instructions.length > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          ACTION ITEMS
        </Typography>
        <List dense disablePadding>
          {o.instructions.map((s, i) => (
            <ListItem key={i} disablePadding sx={{ alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                <CheckCircleOutline
                  fontSize="small"
                  sx={{ color: strategyColor(o.strategy) }}
                />
              </ListItemIcon>
              <ListItemText
                primary={s}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    )}
  </Paper>
);

const TariffEngineeringDetail: FC = () => {
  const params = useParams<{ htsCode: string; countryOfOrigin: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);

  const htsCode = params.htsCode ? decodeURIComponent(params.htsCode) : "";
  const countryOfOrigin = params.countryOfOrigin
    ? decodeURIComponent(params.countryOfOrigin)
    : "";

  const scopeArgs = useMemo(() => {
    const args: { tenantId?: string; clientId?: string } = {};
    const isAdmin = user?.userRole === roles.TDADMIN;
    const isTDAdminTCD =
      isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
    if (selectedTenant?._id) args.tenantId = selectedTenant._id;
    if (selectedClient?._id) {
      args.clientId = selectedClient._id;
    } else if (isTDAdminTCD && selectedTenant?._id) {
      args.clientId = selectedTenant._id;
    }
    return args;
  }, [user, selectedTenant?._id, selectedTenant?.tenantType, selectedClient?._id]);

  const { data, isFetching, refetch } = useGetTariffEngineeringCandidateQuery(
    { htsCode, countryOfOrigin, ...scopeArgs },
    { skip: !htsCode || !countryOfOrigin }
  );
  const [runResearch, { isLoading: isRunning }] =
    useRunTariffEngineeringResearchMutation();

  if (isFetching && !data) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => navigate("/compliance/tariff-engineering")}>← Back</Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          Candidate not found.
        </Alert>
      </Box>
    );
  }

  const analysis = data.latestAnalysis;
  const handleRerun = async () => {
    await runResearch({ htsCode, countryOfOrigin, ...scopeArgs }).unwrap();
    refetch();
  };

  return (
    <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button onClick={() => navigate("/compliance/tariff-engineering")} size="small">
          ← All candidates
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={
            isRunning ? <CircularProgress size={16} /> : <PlayArrow />
          }
          disabled={isRunning}
          onClick={handleRerun}
        >
          {analysis ? "Re-run research" : "Run research"}
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={4} alignItems="baseline">
          <Box>
            <Typography variant="caption" color="text.secondary">
              HTS / Origin
            </Typography>
            <Typography variant="h5" fontFamily="monospace">
              {data.htsCode} / {data.countryOfOrigin}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Current duty
            </Typography>
            <Typography variant="h5">{data.currentDutyRate}%</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total entered value (12mo)
            </Typography>
            <Typography variant="h5">{fmtUsd(data.totalEnteredValue)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Entries / Manufacturers
            </Typography>
            <Typography variant="h5">
              {data.entryCount} / {data.distinctManufacturerCount}
            </Typography>
          </Box>
        </Stack>

        {data.currentDutyBreakdown.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Duty breakdown
            </Typography>
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableBody>
                {data.currentDutyBreakdown.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell>{b.columnName}</TableCell>
                    <TableCell>{b.authority}</TableCell>
                    <TableCell align="right">{b.rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {data.sampleSkus.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Sample SKUs
            </Typography>
            <List dense>
              {data.sampleSkus.map((s, i) => (
                <ListItem key={i} disablePadding>
                  <ListItemText
                    primary={s.name}
                    secondary={
                      <>
                        {s.description}
                        {s.declaredValue !== null && (
                          <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
                            ({fmtUsd(s.declaredValue)})
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {!analysis && (
        <Alert severity="info">
          No research run yet. Click "Run research" to generate opportunities.
        </Alert>
      )}

      {analysis && analysis.status === "FAILED" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Research failed
          </Typography>
          <Typography variant="body2">
            {analysis.error?.message || "Unknown error"}
          </Typography>
        </Alert>
      )}

      {analysis && analysis.status === "DONE" && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Opportunities ({analysis.opportunities.length})
            </Typography>
            {analysis.opportunities.length === 0 ? (
              <Typography color="text.secondary">
                No opportunities found in this run.
              </Typography>
            ) : (
              [...analysis.opportunities]
                .sort((a, b) => b.savingsPct - a.savingsPct)
                .map((o, i) => <OpportunityRow key={i} o={o} />)
            )}
          </Box>

          {analysis.analystNotes && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Analyst Notes
              </Typography>
              <Box
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  fontSize: "0.9rem",
                  m: 0,
                }}
              >
                {analysis.analystNotes}
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="caption" color="text.secondary">
              Run metadata
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5, flexWrap: "wrap" }}>
              <Typography variant="body2">
                Model: <code>{analysis.modelVersion}</code>
              </Typography>
              <Typography variant="body2">
                Duration: {analysis.durationMs}ms
              </Typography>
              <Typography variant="body2">
                Tokens: {analysis.tokenUsage?.prompt} prompt /{" "}
                {analysis.tokenUsage?.completion} completion /{" "}
                {analysis.tokenUsage?.thinking} thinking
              </Typography>
              <Typography variant="body2">
                Tool calls: {analysis.toolCallLog.length}
              </Typography>
            </Stack>
            {analysis.toolCallLog.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ mb: 1 }} />
                {analysis.toolCallLog.map((t, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    sx={{ display: "block", fontFamily: "monospace" }}
                    color={t.ok ? "text.secondary" : "error.main"}
                  >
                    {i + 1}. {t.toolName} {t.durationMs}ms — {t.summary.slice(0, 200)}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TariffEngineeringDetail;
